import "./QuestionBoxAtom.css";
import { loadPendingImage } from "@ui/hooks/usePendingImage";

/**
 * The pill scaffolding shared by the QuestionBox's two atom kinds (video-mention
 * pills and account-mention chips): a `contenteditable="false"` root the browser
 * treats as a single atomic character, a label, and a remove control.
 *
 * Atoms are built imperatively rather than as mounted Solid components because
 * they live inside a browser-managed contenteditable and hold no reactive state.
 * Each kind supplies only what makes it different — its variant class (which
 * carries the colour), its label, its identifying `data-*` attributes and its
 * optional leading visual and marker.
 */

/** Toggled from JS when an atom falls inside the selection; see useQuestionBox. */
export const ATOM_SELECTED_CLASS = "question-box-atom--selected";

/**
 * Marks a child whose icon is drawn by a `::before` pseudo-element in the
 * Material Symbols font, keeping the glyph out of the input's text model.
 */
export const ATOM_GLYPH_CLASS = "question-box-atom__glyph";

export type QuestionBoxAtomOptions = {
	/** Variant suffix selecting the atom's colour, e.g. `video` or `account`. */
	variant: string;
	/** Text shown in the pill; also its tooltip and remove-button label. */
	label: string;
	/**
	 * `data-*` attributes identifying the atom, keyed by their camelCase dataset
	 * name. Undefined values are skipped, so optional fields can be passed as-is.
	 */
	data: Record<string, string | undefined>;
	/** Visual rendered before the label (a thumbnail, an icon glyph host, …). */
	leading?: HTMLElement;
	/** Visual rendered between the leading visual and the label, e.g. a clip's scissors. */
	marker?: HTMLElement;
	onRemove: (element: HTMLElement) => void;
};

/** The glyph host an atom shows in place of an image; styled per variant. */
function leadingGlyph(): HTMLElement {
	const glyph = document.createElement("span");
	glyph.className = `question-box-atom__icon ${ATOM_GLYPH_CLASS}`;
	glyph.setAttribute("aria-hidden", "true");
	return glyph;
}

/**
 * An atom's leading visual: the image at `imageUrl`, or a placeholder glyph in
 * the same box until there is one (each variant sets its own glyph in the CSS
 * beside its factory). The two are interchangeable without changing the atom's
 * width, because an image can arrive after the atom does — a pasted pill's
 * thumbnail is fetched after the fact.
 *
 * A URL that fails to load falls back to the glyph too: an account's avatar can
 * be the platform's own CDN URL, which goes away without warning, and a chip
 * wearing the placeholder reads far better than one wearing a broken image.
 */
export function createAtomLeadingVisual(imageUrl?: string | null): HTMLElement {
	if (!imageUrl) return leadingGlyph();

	const image = document.createElement("img");
	image.className = "question-box-atom__image";
	image.src = imageUrl;
	image.alt = "";
	image.addEventListener("error", () => image.replaceWith(leadingGlyph()), {
		once: true,
	});
	return image;
}

type PendingAtomImage = {
	settled: "loaded" | "failed" | undefined;
	waiting: Set<HTMLElement>;
};

// The editor rebuilds every atom on each edit, so one load per URL is shared
// by the glyphs waiting on it rather than started again for each rebuild.
const pendingAtomImages = new Map<string, PendingAtomImage>();

/**
 * createAtomLeadingVisual for a clip thumbnail that may still be generating:
 * the glyph stays until the image loads, through loadPendingImage's retries.
 */
export function createPendingAtomLeadingVisual(
	imageUrl?: string | null,
): HTMLElement {
	if (!imageUrl) return leadingGlyph();

	let pending = pendingAtomImages.get(imageUrl);
	if (pending?.settled === "loaded") return createAtomLeadingVisual(imageUrl);

	const glyph = leadingGlyph();
	if (pending?.settled === "failed") return glyph;

	if (!pending) {
		const created: PendingAtomImage = { settled: undefined, waiting: new Set() };
		pending = created;
		pendingAtomImages.set(imageUrl, created);
		loadPendingImage(
			imageUrl,
			() => {
				created.settled = "loaded";
				for (const waiting of created.waiting) {
					if (waiting.isConnected) {
						waiting.replaceWith(createAtomLeadingVisual(imageUrl));
					}
				}
				created.waiting.clear();
			},
			() => {
				created.settled = "failed";
				created.waiting.clear();
			},
		);
		// An image that has loaded before settles inside that call.
		if (created.settled === "loaded") return createAtomLeadingVisual(imageUrl);
	}
	for (const waiting of pending.waiting) {
		if (!waiting.isConnected) pending.waiting.delete(waiting);
	}
	pending.waiting.add(glyph);
	return glyph;
}

/**
 * Swaps an atom's placeholder glyph for the image at `imageUrl`, for a picture
 * that arrives after the atom was built. An atom that is already showing an
 * image is left alone, so an unchanged URL is never re-fetched.
 */
export function setAtomLeadingImage(atom: HTMLElement, imageUrl: string): void {
	const leading = atom.firstElementChild;
	if (!leading?.classList.contains("question-box-atom__icon")) return;
	leading.replaceWith(createAtomLeadingVisual(imageUrl));
}

export function createQuestionBoxAtom(
	options: QuestionBoxAtomOptions,
): HTMLElement {
	const atom = document.createElement("span");
	atom.className = `question-box-atom question-box-atom--${options.variant}`;
	atom.contentEditable = "false";
	atom.title = options.label;
	for (const [key, value] of Object.entries(options.data)) {
		if (value !== undefined) atom.dataset[key] = value;
	}

	if (options.leading) atom.appendChild(options.leading);
	if (options.marker) atom.appendChild(options.marker);

	const label = document.createElement("span");
	label.className = "question-box-atom__label";
	label.textContent = options.label;
	atom.appendChild(label);

	const remove = document.createElement("span");
	remove.className = `question-box-atom__remove ${ATOM_GLYPH_CLASS}`;
	remove.setAttribute("role", "button");
	remove.setAttribute("aria-label", `Remove ${options.label}`);
	// Keep focus/selection in the input when the remove control is pressed.
	remove.addEventListener("mousedown", (event) => event.preventDefault());
	remove.addEventListener("click", (event) => {
		event.preventDefault();
		event.stopPropagation();
		options.onRemove(atom);
	});
	atom.appendChild(remove);

	return atom;
}
